/**
 * Blossom — slider.js
 * <slider-component> — lightweight carousel on native CSS scroll-snap.
 * Touch/trackpad scrolling is native; this adds arrows, dots, mouse drag,
 * keyboard support, autoplay (with pause control) and reduced-motion support.
 *
 * Markup (see component-slider.css for layout variables):
 *   <slider-component data-autoplay="5" data-loop="true" role="region" aria-roledescription="carousel" aria-label="…">
 *     <ul class="slider__track" data-slider-track tabindex="0">
 *       <li class="slider__slide" data-slide>…</li>
 *     </ul>
 *     <div class="slider__controls">
 *       <button data-slider-prev aria-label="…">…</button>
 *       <div class="slider__dots" data-slider-dots></div>
 *       <button data-slider-next aria-label="…">…</button>
 *       <button data-slider-autoplay aria-label="…">…</button>   (optional)
 *     </div>
 *   </slider-component>
 */
(() => {
  if (!window.Theme || customElements.get('slider-component')) return;

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track || this.initialized) return;
      this.initialized = true;

      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dotsContainer = this.querySelector('[data-slider-dots]');
      this.autoplayButton = this.querySelector('[data-slider-autoplay]');
      this.autoplaySeconds = Number(this.dataset.autoplay || 0);
      this.loop = this.dataset.loop === 'true' || this.autoplaySeconds > 0;

      this.labelSlides();
      this.prevButton?.addEventListener('click', () => this.go(-1));
      this.nextButton?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', Theme.debounce(() => this.update(), 60), { passive: true });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event));
      this.bindDrag();

      this.resizeObserver = new ResizeObserver(Theme.debounce(() => this.refresh(), 100));
      this.resizeObserver.observe(this.track);

      if (this.autoplaySeconds > 0) this.setupAutoplay();
      this.refresh();

      if (Theme.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          this.pause(true);
          this.scrollToSlide(event.target.closest('[data-slide]') || event.target);
        });
        this.addEventListener('shopify:block:deselect', () => {
          this.playing = this.autoplaySeconds > 0;
          this.resume();
        });
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearInterval(this.timer);
    }

    get slides() {
      return Array.from(this.track.querySelectorAll(':scope > [data-slide]')).filter(
        (slide) => slide.getClientRects().length > 0
      );
    }

    get slideStep() {
      const [first, second] = this.slides;
      if (!first) return this.track.clientWidth || 1;
      if (!second) return first.getBoundingClientRect().width || 1;
      return Math.abs(second.offsetLeft - first.offsetLeft) || 1;
    }

    get perPage() {
      return Math.max(1, Math.floor((this.track.clientWidth + 8) / this.slideStep));
    }

    get pageCount() {
      const slides = this.slides.length;
      if (slides <= this.perPage) return 1;
      return Math.ceil((slides - this.perPage) / this.perPage) + 1;
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get currentPage() {
      if (this.track.scrollLeft >= this.maxScroll - 4) return this.pageCount - 1;
      return Math.round(this.track.scrollLeft / (this.slideStep * this.perPage));
    }

    labelSlides() {
      const slides = this.slides;
      const template = Theme.strings.slideLabel || '[index] of [count]';
      slides.forEach((slide, index) => {
        // List items keep their list semantics; other slides become labelled groups.
        if (slide.tagName !== 'LI') {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', Theme.strings.slide || 'slide');
        }
        if (!slide.hasAttribute('aria-label')) {
          slide.setAttribute('aria-label', template.replace('[index]', index + 1).replace('[count]', slides.length));
        }
      });
    }

    refresh() {
      const scrollable = this.maxScroll > 4;
      this.classList.toggle('is-static', !scrollable);
      this.renderDots();
      this.update();
    }

    renderDots() {
      if (!this.dotsContainer) return;
      const count = this.pageCount;
      if (this.dotsContainer.childElementCount === count) return;
      const template = Theme.strings.goToSlide || 'Go to slide [index]';
      this.dotsContainer.replaceChildren(
        ...Array.from({ length: count }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'slider__dot';
          dot.setAttribute('aria-label', template.replace('[index]', index + 1));
          dot.addEventListener('click', () => this.goToPage(index));
          return dot;
        })
      );
      this.dotsContainer.hidden = count < 2;
    }

    update() {
      const page = this.currentPage;
      const left = this.track.scrollLeft;
      if (this.prevButton) this.prevButton.disabled = !this.loop && left <= 4;
      if (this.nextButton) this.nextButton.disabled = !this.loop && left >= this.maxScroll - 4;
      this.dotsContainer?.querySelectorAll('.slider__dot').forEach((dot, index) => {
        if (index === page) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const trackWidth = this.track.clientWidth;
      this.slides.forEach((slide) => {
        const offset = slide.offsetLeft - this.track.offsetLeft - left;
        const visible = offset > -slide.offsetWidth / 2 && offset < trackWidth - slide.offsetWidth / 2;
        slide.classList.toggle('is-visible', visible);
      });
      if (page !== this.lastPage) {
        this.lastPage = page;
        this.dispatchEvent(new CustomEvent('slider:change', { detail: { page }, bubbles: true }));
      }
    }

    go(direction) {
      const atEnd = this.track.scrollLeft >= this.maxScroll - 4;
      const atStart = this.track.scrollLeft <= 4;
      if (this.loop && direction > 0 && atEnd) return this.scrollTo(0);
      if (this.loop && direction < 0 && atStart) return this.scrollTo(this.maxScroll);
      this.scrollTo(this.track.scrollLeft + direction * this.slideStep * this.perPage);
    }

    goToPage(index) {
      this.scrollTo(index * this.slideStep * this.perPage);
    }

    scrollToSlide(slide) {
      if (!slide || !this.track.contains(slide)) return;
      this.scrollTo(slide.offsetLeft - this.track.offsetLeft);
    }

    scrollTo(left) {
      this.track.scrollTo({ left, behavior: Theme.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    onKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      }
    }

    /** Mouse drag-to-scroll (touch devices use native scrolling). */
    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('input, select, textarea, button')) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
      });

      this.onPointerMove = (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (!moved && Math.abs(delta) > 5) {
          moved = true;
          this.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startScroll - delta;
      };

      this.onPointerUp = () => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        this.classList.remove('is-dragging');
        const step = this.slideStep;
        this.scrollTo(Math.round(this.track.scrollLeft / step) * step);
        // Let the click that ends a drag be swallowed, then reset.
        setTimeout(() => {
          moved = false;
        }, 0);
      };

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);

      this.track.addEventListener(
        'click',
        (event) => {
          if (!moved) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    /* ---------------------------------------------------------- Autoplay */
    setupAutoplay() {
      if (Theme.prefersReducedMotion()) {
        this.autoplayButton?.setAttribute('hidden', '');
        return;
      }
      this.playing = true;
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      });
      this.autoplayButton?.addEventListener('click', () => {
        this.playing = !this.playing;
        this.autoplayButton.setAttribute('aria-pressed', String(!this.playing));
        this.autoplayButton.setAttribute(
          'aria-label',
          this.playing ? Theme.strings.pauseSlideshow || 'Pause slideshow' : Theme.strings.playSlideshow || 'Play slideshow'
        );
        if (this.playing) this.resume();
        else this.pause(true);
      });
      this.resume();
    }

    pause(hard = false) {
      clearInterval(this.timer);
      this.timer = null;
      if (hard) this.playing = false;
    }

    resume() {
      if (!this.playing || !this.autoplaySeconds || this.timer) return;
      this.timer = setInterval(() => this.go(1), this.autoplaySeconds * 1000);
    }
  }

  customElements.define('slider-component', SliderComponent);
})();
